import {
  computed,
  type ComputedRef,
  type MaybeRefOrGetter,
  toValue,
} from 'vue';

import useAuth from '~/store/auth/useAuth';

// eslint-disable-next-line no-unused-vars
type GroupMap<T> = { [Key in keyof T]: ComputedRef<boolean> };

export default function useGroup<
  T extends Record<string, string[]> | string[],
>(
  groupsOrMap: MaybeRefOrGetter<T>,
): T extends string[] ? { hasAccess: ComputedRef<boolean> } : GroupMap<T> {
  const { data: auth } = useAuth();
  const value = computed(() => toValue(groupsOrMap));

  const hasAccess = computed(() => {
    const resolvedValue = value.value;
    if (Array.isArray(resolvedValue)) {
      return auth.value?.hasSomeGroup?.(resolvedValue) ?? false;
    }
    return false;
  });

  const resolvedValue = value.value;
  if (Array.isArray(resolvedValue)) {
    return { hasAccess } as T extends string[]
      ? { hasAccess: ComputedRef<boolean> }
      : GroupMap<T>;
  }

  const map = resolvedValue as Record<string, string[]>;
  return Object.fromEntries(
    Object.keys(map).map((key) => [
      key,
      computed(() => auth.value?.hasSomeGroup?.(map[key]) ?? false),
    ]),
  ) as T extends string[] ? { hasAccess: ComputedRef<boolean> } : GroupMap<T>;
}
